import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightToBracket,
  faArrowRightFromBracket,
  faUser,
  faGauge,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle, faFacebookF } from "@fortawesome/free-brands-svg-icons";
import { useAuth } from "../../../context/AuthContext";
import styles from "./AuthMenu.module.css";

const POST_LOGIN_PATH = process.env.REACT_APP_POST_LOGIN_PATH || "/app/dashboard";
const POST_LOGIN_KEY = "pp:postLogin";
const INVITE_KEY = "pp:invite";

export default function AuthMenu({ variant = "inline" }) {
  const { user, loginWithGoogle, logout, isFirebaseReady, firebaseError, betaStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Read order: LOCAL first (survives OAuth hop), then session
  const [inviteCode, setInviteCode] = useState(
    () => localStorage.getItem(INVITE_KEY) || sessionStorage.getItem(INVITE_KEY) || ""
  );

  const desiredDest = useMemo(() => {
    return (
      location.state?.from?.pathname ||
      (location.pathname.startsWith("/app") ? location.pathname : POST_LOGIN_PATH)
    );
  }, [location]);

  // Navigate only AFTER beta verification passes
  useEffect(() => {
    if (!user) return;
    if (betaStatus !== "ok") return;

    const stored = localStorage.getItem(POST_LOGIN_KEY) || sessionStorage.getItem(POST_LOGIN_KEY);
    if (stored && location.pathname !== stored) {
      localStorage.removeItem(POST_LOGIN_KEY);
      sessionStorage.removeItem(POST_LOGIN_KEY);
      navigate(stored, { replace: true });
    }
  }, [user, betaStatus, navigate, location.pathname]);

  const handleLogin = () => {
    if (!inviteCode.trim()) return;
    // write to LOCAL first, then mirror to session (optional)
    localStorage.setItem(INVITE_KEY, inviteCode);
    sessionStorage.setItem(INVITE_KEY, inviteCode);

    localStorage.setItem(POST_LOGIN_KEY, desiredDest);
    sessionStorage.setItem(POST_LOGIN_KEY, desiredDest);

    // direct call to preserve the user gesture for popup (desktop)
    loginWithGoogle();
  };

  const handleGoToApp = () => navigate(POST_LOGIN_PATH);

  /* ============== NAVBAR VARIANT ============== */
  if (variant === "nav") {
    if (!user) return null;
    const avatarUrl = user.photoURL;
    const displayName = user.displayName || user.email || "Account";
    const canGoToApp = betaStatus === "ok";

    return (
      <div className={`dropdown ${styles.navWrap}`}>
        <button
          className={`btn btn-outline-secondary btn-sm dropdown-toggle d-flex align-items-center ${styles.navBtn}`}
          type="button"
          id="ppAuthNavBtn"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-label="Account menu"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className={`${styles.navAvatar} me-2`} />
          ) : (
            <span className={`me-2 ${styles.navAvatarFallback}`}>
              <FontAwesomeIcon icon={faUser} />
            </span>
          )}
          <span className="text-truncate">{displayName}</span>
        </button>

        <ul className="dropdown-menu dropdown-menu-end shadow-sm" aria-labelledby="ppAuthNavBtn">
          <li className="px-3 py-2">
            <div className="small text-muted">
              {betaStatus === "ok" ? "Signed in" : "Verifying invite…"}
            </div>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleGoToApp}
              disabled={!canGoToApp}
              title={canGoToApp ? "" : "Your invite is still being verified"}
            >
              <FontAwesomeIcon icon={faGauge} className="me-2" />
              Go to App
            </button>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center text-danger"
              onClick={logout}
            >
              <FontAwesomeIcon icon={faArrowRightFromBracket} className="me-2" />
              Sign out
            </button>
          </li>
        </ul>
      </div>
    );
  }

  /* ===== Disabled / error surface ===== */
  if (!isFirebaseReady && firebaseError) {
    return (
      <div className={variant === "card" ? styles.stack : styles.inline}>
        <div>
          <label htmlFor="ppInviteInput" className="form-label">Invitation code</label>
          <input id="ppInviteInput" className="form-control" placeholder="e.g. PP-BETA-XXXX" disabled aria-label="Invitation code" />
        </div>
        <button className="btn btn-outline-secondary" title={firebaseError} disabled>
          <FontAwesomeIcon icon={faRightToBracket} className="me-2" />
          Continue with Google
        </button>
      </div>
    );
  }

  /* ============== LOGGED OUT ============== */
  if (!user) {
    const hasInvite = inviteCode.trim().length > 0;

    if (variant === "card") {
      return (
        <form className={styles.form} onSubmit={(e) => { e.preventDefault(); if (hasInvite) handleLogin(); }}>
          {/* Invite first */}
          <div className="mb-3">
            <label htmlFor="ppInviteInput" className="form-label">Invitation code</label>
            <input
              id="ppInviteInput"
              type="text"
              className="form-control"
              placeholder="e.g. PP-BETA-XXXX"
              value={inviteCode}
              onChange={(e) => {
                const v = e.target.value || "";
                setInviteCode(v);
                localStorage.setItem(INVITE_KEY, v);
                sessionStorage.setItem(INVITE_KEY, v);
              }}
              onKeyDown={(e) => { if (e.key === "Enter" && hasInvite) handleLogin(); }}
              autoComplete="one-time-code"
              inputMode="text"
            />
            <small className="text-muted">Enter your invite to unlock sign-in.</small>
          </div>

          {/* Providers */}
          <div className="d-grid gap-2 mb-2">
            <button
              id="ppInviteContinueBtn"
              type="submit"
              className={`btn w-100 ${hasInvite ? styles.btnPrimaryGrad : "btn-outline-secondary"}`}
              disabled={!hasInvite}
              aria-disabled={!hasInvite}
            >
              <FontAwesomeIcon icon={faGoogle} className="me-2" />
              Continue with Google
            </button>
            <button type="button" className="btn btn-outline-secondary w-100" disabled aria-disabled="true">
              <FontAwesomeIcon icon={faFacebookF} className="me-2" />
              Continue with Facebook
            </button>
          </div>

          {/* Divider */}
          <div className={styles.divider} role="separator" aria-label="or with email">
            <span>or use email</span>
          </div>

          {/* Email/password disabled */}
          <fieldset disabled aria-disabled="true">
            <div className="mb-2">
              <label htmlFor="ppEmail" className="form-label">Email</label>
              <input id="ppEmail" type="email" className="form-control" placeholder="you@example.com" />
            </div>
            <div className="mb-3">
              <label htmlFor="ppPassword" className="form-label">Password</label>
              <input id="ppPassword" type="password" className="form-control" placeholder="••••••••" />
            </div>
            <button type="button" className="btn btn-secondary w-100" title="Email sign-in is disabled in private beta">
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Sign in with Email
            </button>
          </fieldset>
        </form>
      );
    }

    // inline (unchanged)
    return (
      <div className={styles.inline}>
        <input
          id="ppInviteInput"
          type="text"
          className="form-control form-control-sm"
          placeholder="Invitation code"
          value={inviteCode}
          onChange={(e) => {
            const v = e.target.value || "";
            setInviteCode(v);
            localStorage.setItem(INVITE_KEY, v);
            sessionStorage.setItem(INVITE_KEY, v);
          }}
          onKeyDown={(e) => { if (e.key === "Enter" && hasInvite) handleLogin(); }}
          style={{ width: 180 }}
          aria-label="Invitation code"
          autoComplete="one-time-code"
          inputMode="text"
        />
        <button
          id="ppInviteContinueBtn"
          className="btn btn-primary btn-sm"
          type="button"
          onClick={handleLogin}
          disabled={!inviteCode.trim()}
        >
          <FontAwesomeIcon icon={faRightToBracket} className="me-2" />
          Continue with Google
        </button>
      </div>
    );
  }

  /* ============== LOGGED IN (non-nav) ============== */
  const avatarUrl = user.photoURL;
  const displayName = user.displayName || user.email || "Account";

  return (
    <div className={styles.loggedIn}>
      <div className="d-flex align-items-center gap-2 mb-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className={styles.avatar} />
        ) : (
          <span className={`me-2 ${styles.avatarFallback}`}><FontAwesomeIcon icon={faUser} /></span>
        )}
        <div className="text-truncate">
          <div className="fw-semibold">{displayName}</div>
          <small className="text-muted">{betaStatus === "ok" ? "Signed in" : "Verifying invite…"}</small>
        </div>
      </div>

      <div className="d-grid gap-2">
        <button className="btn btn-outline-secondary" onClick={handleGoToApp} disabled={betaStatus !== "ok"}>
          <FontAwesomeIcon icon={faGauge} className="me-2" />
          Go to App
        </button>
        <button className="btn btn-outline-danger" onClick={logout}>
          <FontAwesomeIcon icon={faArrowRightFromBracket} className="me-2" />
          Sign out
        </button>
      </div>
    </div>
  );
}
